<script setup lang="ts">
  import { computed, useId } from 'vue';
  import { useLocale } from '@tundr/i18n';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import type { PaginationProps } from './Pagination.types';
  import { PaginationType } from './Pagination.types';
  import Button from '../Button/Button.vue';
  import { ButtonVariant, ButtonSize } from '../Button/Button.types';
  import {
    containerClass,
    containerDotsClass,
    dotItemsClass,
    numericItemsClass,
    dotDefaultClass,
    dotSelectedClass,
    numericDefaultClass,
    numericSelectedClass,
    ellipsisClass,
  } from './Pagination.css';
  import { buildPaginationNumericItems } from './paginationNumericItems';

  const { t } = useLocale('ds-components');

  const props = withDefaults(defineProps<PaginationProps>(), {
    currentPage: 1,
    disabled: false,
  });

  const uid = useId();
  const resolvedTestId = computed(() => props.testId ?? `ds-pagination-${uid}`);

  const emit = defineEmits<{
    pageChange: [page: number];
  }>();

  const computedAriaLabel = computed(
    () => props.ariaLabel ?? t('pagination.aria_label'),
  );
  const computedPrevAriaLabel = computed(
    () => props.prevAriaLabel ?? t('pagination.prev_aria_label'),
  );
  const computedNextAriaLabel = computed(
    () => props.nextAriaLabel ?? t('pagination.next_aria_label'),
  );
  const getPageAriaLabel = (page: number) =>
    t('pagination.page_aria_label', { page });

  const clampedPage = computed(() =>
    Math.min(Math.max(1, props.currentPage), props.totalPages),
  );
  const isFirstPage = computed(() => clampedPage.value <= 1);
  const isLastPage = computed(() => clampedPage.value >= props.totalPages);
  const isDot = computed(() => props.type === PaginationType.CarouselDots);
  const isNumeric = computed(
    () => props.type === PaginationType.DatasetNumbers,
  );

  const numericItems = computed(() =>
    buildPaginationNumericItems(props.totalPages, clampedPage.value),
  );

  const onPageClick = (page: number) => {
    if (props.disabled || page === clampedPage.value) return;
    emit('pageChange', page);
  };

  const onPrev = () => {
    if (props.disabled || isFirstPage.value) return;
    emit('pageChange', clampedPage.value - 1);
  };

  const onNext = () => {
    if (props.disabled || isLastPage.value) return;
    emit('pageChange', clampedPage.value + 1);
  };
</script>

<template>
  <nav
    v-if="totalPages > 1"
    :class="isDot ? containerDotsClass : containerClass"
    :aria-label="computedAriaLabel"
    :data-testid="resolvedTestId"
  >
    <!-- Prev button -->
    <Button
      :variant="ButtonVariant.Icon"
      :icon-name="TUNDR_ICONS.CHEVRON_LEFT"
      :size="ButtonSize.MD"
      :disabled="disabled || isFirstPage"
      is-ghost
      :aria-label="computedPrevAriaLabel"
      :test-id="`${resolvedTestId}-prev`"
      @click="onPrev"
    />

    <!-- Dot variant (carousel) -->
    <div v-if="isDot" :class="dotItemsClass">
      <button
        v-for="page in totalPages"
        :key="page"
        :class="
          page === clampedPage ? dotSelectedClass : dotDefaultClass
        "
        :aria-label="getPageAriaLabel(page)"
        :aria-current="page === clampedPage ? 'page' : undefined"
        :disabled="disabled"
        type="button"
        :data-testid="`${resolvedTestId}-dot-${page}`"
        @click="onPageClick(page)"
      />
    </div>

    <!-- Numeric variant (dataset) -->
    <div v-else-if="isNumeric" :class="numericItemsClass">
      <template v-for="item in numericItems" :key="item">
        <span
          v-if="item === 'ellipsis-start' || item === 'ellipsis-end'"
          :class="ellipsisClass"
          aria-hidden="true"
          :data-testid="`${resolvedTestId}-${item}`"
        >
          ...
        </span>
        <button
          v-else
          :class="
            item === clampedPage
              ? numericSelectedClass
              : numericDefaultClass
          "
          :aria-label="getPageAriaLabel(item as number)"
          :aria-current="item === clampedPage ? 'page' : undefined"
          :disabled="disabled"
          type="button"
          :data-testid="`${resolvedTestId}-page-${item}`"
          @click="onPageClick(item as number)"
        >
          {{ item }}
        </button>
      </template>
    </div>

    <!-- Next button -->
    <Button
      :variant="ButtonVariant.Icon"
      :icon-name="TUNDR_ICONS.CHEVRON_RIGHT"
      :size="ButtonSize.MD"
      is-ghost
      :disabled="disabled || isLastPage"
      :aria-label="computedNextAriaLabel"
      :test-id="`${resolvedTestId}-next`"
      @click="onNext"
    />
  </nav>
</template>
